import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, X } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { orpc, rpcClient } from "@/lib/rpc";

function initials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return `${parts[0]![0]}${parts[parts.length - 1]![0]}`.toUpperCase();
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function BuddyPoolTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const settingsQuery = useQuery(orpc.onboardings.getBuddySettings.queryOptions());
  const poolQuery = useQuery(orpc.onboardings.listBuddyPool.queryOptions());

  const pool = poolQuery.data ?? [];
  const cap = settingsQuery.data?.maxActivePerBuddy ?? 1;

  const [capInput, setCapInput] = useState("");
  useEffect(() => {
    if (settingsQuery.data) setCapInput(String(settingsQuery.data.maxActivePerBuddy));
  }, [settingsQuery.data]);

  const updateSettings = useMutation({
    mutationFn: (n: number) => rpcClient.onboardings.updateBuddySettings({ maxActivePerBuddy: n }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.getBuddySettings.queryOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.listBuddyCandidates.queryOptions().queryKey,
      });
      toast({ title: "Limite atualizado" });
    },
    onError: (err: any) =>
      toast({
        title: "Erro ao atualizar limite",
        description: err?.message ?? "Tente novamente.",
        variant: "destructive",
      }),
  });

  function handleSaveCap() {
    const n = Number.parseInt(capInput, 10);
    if (!Number.isFinite(n) || n < 1 || n > 10) {
      toast({
        title: "Valor inválido",
        description: "Informe um número entre 1 e 10.",
        variant: "destructive",
      });
      return;
    }
    updateSettings.mutate(n);
  }

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const debouncedSearch = useDebounced(searchText, 250);

  const searchQuery = useQuery({
    ...orpc.onboardings.searchNonBuddies.queryOptions({ input: { query: debouncedSearch } }),
    enabled: searchOpen,
  });
  const searchResults = searchQuery.data ?? [];

  const addBuddy = useMutation({
    mutationFn: (employeeId: string) => rpcClient.onboardings.addBuddy({ employeeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.listBuddyPool.queryOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.searchNonBuddies.queryOptions({ input: { query: "" } }).queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.listBuddyCandidates.queryOptions().queryKey,
      });
      setSearchText("");
      setSearchOpen(false);
      toast({ title: "Buddy adicionado ao pool" });
    },
    onError: (err: any) =>
      toast({
        title: "Erro ao adicionar buddy",
        description: err?.message ?? "Tente novamente.",
        variant: "destructive",
      }),
  });

  const removeBuddy = useMutation({
    mutationFn: (employeeId: string) => rpcClient.onboardings.removeBuddy({ employeeId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.listBuddyPool.queryOptions().queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: orpc.onboardings.listBuddyCandidates.queryOptions().queryKey,
      });
      toast({ title: "Buddy removido do pool" });
    },
    onError: (err: any) => {
      const code = err?.data?.code;
      if (code === "buddy_has_active_onboardings") {
        toast({
          title: "Não é possível remover",
          description: err?.message ?? "Este buddy ainda está acompanhando onboardings ativos.",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Erro ao remover buddy",
          description: err?.message ?? "Tente novamente.",
          variant: "destructive",
        });
      }
    },
  });

  const sortedPool = useMemo(
    () =>
      [...pool].sort((a, b) => {
        if (a.activeOnboardingCount !== b.activeOnboardingCount) {
          return a.activeOnboardingCount - b.activeOnboardingCount;
        }
        return a.fullName.localeCompare(b.fullName, "pt-BR");
      }),
    [pool],
  );

  return (
    <div className="mt-6 space-y-6">
      <Card data-testid="card-buddy-settings">
        <CardHeader>
          <CardTitle className="text-base">Configuração</CardTitle>
        </CardHeader>
        <CardContent className="flex items-end gap-3">
          <div>
            <Label htmlFor="buddy-cap" className="text-xs">
              Máximo de onboardings ativos por buddy
            </Label>
            <Input
              id="buddy-cap"
              type="number"
              min={1}
              max={10}
              value={capInput}
              onChange={(e) => setCapInput(e.target.value)}
              disabled={updateSettings.isPending}
              className="w-32"
              data-testid="input-buddy-cap"
            />
          </div>
          <Button
            onClick={handleSaveCap}
            disabled={updateSettings.isPending || capInput === String(cap)}
            data-testid="button-save-buddy-cap"
          >
            {updateSettings.isPending ? "Salvando..." : "Salvar"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="space-y-3">
          <div>
            <CardTitle className="text-base">Pool de buddies</CardTitle>
            <p className="mt-1 text-xs text-muted-foreground">
              Colaboradores aptos a acompanhar novos contratados.
            </p>
          </div>
          <Popover open={searchOpen} onOpenChange={setSearchOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="w-fit" data-testid="button-add-buddy">
                <Plus className="mr-2 h-4 w-4" /> Adicionar buddy
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[360px] p-0" align="start">
              <div className="flex items-center border-b px-3">
                <Search className="mr-2 h-4 w-4 text-muted-foreground" />
                <input
                  // biome-ignore lint/a11y/noAutofocus: autofocus is desired for this search popover
                  autoFocus
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Buscar colaborador..."
                  className="flex h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                  data-testid="input-search-buddy"
                />
              </div>
              <div className="max-h-72 overflow-auto py-1">
                {searchQuery.isLoading ? (
                  <p className="px-3 py-4 text-sm text-muted-foreground">Buscando...</p>
                ) : searchResults.length === 0 ? (
                  <p className="px-3 py-4 text-sm text-muted-foreground">
                    Nenhum colaborador encontrado.
                  </p>
                ) : (
                  searchResults.map((e) => (
                    <button
                      key={e.id}
                      type="button"
                      onClick={() => addBuddy.mutate(e.id)}
                      disabled={addBuddy.isPending}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-accent disabled:opacity-50"
                      data-testid={`search-result-${e.id}`}
                    >
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
                        {initials(e.fullName)}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{e.fullName}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {e.corporateEmail}
                          {e.department ? ` · ${e.department}` : ""}
                        </span>
                      </span>
                    </button>
                  ))
                )}
              </div>
            </PopoverContent>
          </Popover>
        </CardHeader>
        <CardContent className="p-0">
          {poolQuery.isLoading ? (
            <p className="p-6 text-sm text-muted-foreground">Carregando...</p>
          ) : sortedPool.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              Nenhum buddy adicionado ao pool. Use a busca acima para adicionar colaboradores.
            </p>
          ) : (
            <ul className="divide-y">
              {sortedPool.map((b) => {
                const available = b.activeOnboardingCount === 0;
                const atCap = b.activeOnboardingCount >= cap;
                return (
                  <li
                    key={b.id}
                    className="group flex items-center gap-3 px-4 py-3"
                    data-testid={`row-buddy-${b.id}`}
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium">
                      {initials(b.fullName)}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{b.fullName}</div>
                      <div className="truncate text-xs text-muted-foreground">
                        {b.corporateEmail}
                        {b.department ? ` · ${b.department}` : ""}
                      </div>
                    </div>
                    {available ? (
                      <span className="text-[13px] font-semibold text-[hsl(var(--forest-pulse))]">
                        Disponível
                      </span>
                    ) : (
                      <span
                        className={
                          atCap
                            ? "text-[13px] font-semibold text-[hsl(var(--comp-coral))]"
                            : "text-[13px] font-semibold text-[hsl(var(--amber-state))]"
                        }
                      >
                        Com {b.currentHireNames.join(", ")}
                      </span>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeBuddy.mutate(b.id)}
                      disabled={removeBuddy.isPending || b.activeOnboardingCount > 0}
                      className="opacity-0 transition-opacity group-hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-30"
                      data-testid={`button-remove-${b.id}`}
                      title={
                        b.activeOnboardingCount > 0
                          ? "Buddy tem onboardings ativos — não pode ser removido"
                          : "Remover do pool"
                      }
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
